import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import styles from "./EditRolePage.module.scss";
import { AxiosResponse, AxiosErrorType } from "../../../src/types";

interface FormValues {
  name: string;
  permissions: Record<string, boolean>;
}

const EditOrgRolePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formValues, setFormValues] = useState<FormValues>({
    name: "",
    permissions: {},
  });
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.post<AxiosResponse<{ roles: any[] }>>("/core/role/orgroletype/get", {});
        const roleTypeData = response.data.roles?.find((rt) => rt.id === parseInt(id || "0"));
        if (!roleTypeData) {
          console.error("Organization role type not found for id:", id);
          throw new Error("Organization role type not found");
        }
        setFormValues({
          name: roleTypeData.name,
          permissions: roleTypeData.permissions || {},
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "Failed to fetch organization role type";
        console.error("Fetch organization role type failed:", err.message, err.response?.data);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setFormValues((prev) => ({
        ...prev,
        permissions: JSON.parse(e.target.value || "{}"),
      }));
      setError("");
    } catch (err) {
      setError("Invalid JSON format for permissions");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const response = await api.post("/core/role/orgroletype/update", {
        orgroletype_id: parseInt(id || "0"),
        name: formValues.name,
        permissions: formValues.permissions,
      });
      console.log("Organization role type updated:", response.data);
      navigate("/roles");
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to update organization role type";
      console.error("Update organization role type failed:", err.message, err.response?.data);
      setError(errorMessage);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <h2>Edit Organization Role Type</h2>
      {error && <div className={styles.error}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Role Type Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            required
            className={styles.editableInput}
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="permissions">Permissions (JSON)</label>
          <input
            type="text"
            id="permissions"
            name="permissions"
            value={JSON.stringify(formValues.permissions)}
            onChange={handlePermissionsChange}
            placeholder='{"view": true, "edit": false}'
            className={styles.editableInput}
          />
        </div>
        <button type="submit">Update Role Type</button>
      </form>
    </div>
  );
};

export default EditOrgRolePage;
